import React, { useState, useEffect } from 'react';
import { WheelSafeNumberInput } from '../common/WheelSafeNumberInput';
import {
  Create,
  SimpleForm,
  TextInput,
  SelectInput,
  DateTimeInput,
  BooleanInput,
  SelectArrayInput,
  required,
  minValue,
  useTranslate,
  useNotify,
  useRedirect,
  Toolbar,
  SaveButton,
  FormDataConsumer,
  RadioButtonGroupInput,
  useDataProvider,
} from 'react-admin';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  IconButton,
  Alert,
  Autocomplete,
  TextField as MuiTextField,
  Checkbox,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  LocalOffer,
  Settings,
  People,
  Schedule,
  AttachMoney,
  LocationOn,
  ArrowBack,
  TrendingUp,
  Block,
} from '@mui/icons-material';
import { useFormContext } from 'react-hook-form';
import { PromotionMultiLanguageFields } from './PromotionMultiLanguageFields';
import { PromotionTermsManager } from './PromotionTermsManager';
import { ServerErrorBinder } from '../common/ServerErrorBinder';
import { translateServerMessage } from '../../utils/translateServerMessage';

const PROMOTION_KNOWN_FIELDS = new Set([
    'name', 'description', 'code', 'promotionType', 'codeType',
    'discountType', 'discountValue', 'userType', 'membershipLevels',
    'validFrom', 'validUntil', 'timeOfDayStart', 'timeOfDayEnd',
    'daysOfWeek', 'minChargeAmount', 'maxUsesTotal', 'maxUsesPerUser',
    'priority', 'stackableWithCoupons', 'stackableWithPromotions',
    'isActive', 'sites', 'chargerTypes',
]);

// Backend emits snake_case paths (e.g. 'valid_until', 'code_type'); the React
// Admin form uses camelCase sources. Convert snake_case → camelCase so the
// error binds to the correct input.
const snakeToCamel = (s: string): string =>
    s.replace(/_([a-z])/g, (_m, c) => c.toUpperCase());

const mapPromotionBackendPath = (path: string): string => {
    const clean = path.replace(/^body\./, '').replace(/^params\./, '').replace(/^query\./, '');
    return clean.split('.').map(snakeToCamel).join('.');
};

const promotionFieldLabelKey = (field: string): string => {
    const head = field.split('.')[0];
    return PROMOTION_KNOWN_FIELDS.has(head)
        ? `resources.promotions.fields.${head}`
        : field;
};

const CreateToolbar = () => {
  return (
    <Toolbar
      sx={{
        backgroundColor: '#FAFAFA',
        borderTop: '1px solid #E0E0E0',
        px: { xs: 2, sm: 3 },
        py: 2,
        mt: 4,
        '& .MuiButton-root': {
          borderRadius: 2,
          textTransform: 'none',
          fontWeight: 600,
          px: 3,
          py: 1.5,
        },
      }}
    >
      <SaveButton
        variant="contained"
        sx={{
          backgroundColor: '#00C853',
          '&:hover': {
            backgroundColor: '#00A843',
            boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
          },
        }}
      />
    </Toolbar>
  );
};

const PageHeader = () => {
  const redirect = useRedirect();
  const translate = useTranslate();

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      mb: 4,
    }}>
      <IconButton
        onClick={() => redirect('/promotions')}
        size="large"
        sx={{
          backgroundColor: '#FFFFFF',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          '&:hover': {
            backgroundColor: '#F5F5F5',
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
          },
        }}
        title={translate('resources.promotions.create.backToList')}
      >
        <ArrowBack />
      </IconButton>
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
          {translate('resources.promotions.create.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {translate('resources.promotions.create.subtitle')}
        </Typography>
      </Box>
    </Box>
  );
};

// Custom Site Selector Component
const SiteSelector = () => {
  const { setValue, watch } = useFormContext();
  const dataProvider = useDataProvider();
  const translate = useTranslate();
  const [sites, setSites] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const selectedSiteIds = watch('sites') || [];

  useEffect(() => {
    loadSites();
  }, []);

  const loadSites = async () => {
    try {
      setLoading(true);
      const { data } = await dataProvider.getList('sites', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'name', order: 'ASC' },
        filter: {},
      });
      setSites(data);
    } catch (error) {
      console.error('Error loading sites:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectedSites = sites.filter(site => selectedSiteIds.includes(site.id));

  return (
    <Autocomplete
      multiple
      options={sites}
      value={selectedSites}
      onChange={(event, value) => {
        setValue('sites', value.map(site => site.id));
      }}
      loading={loading}
      getOptionLabel={(option) => option.name || ''}
      renderOption={(props, option, { selected }) => (
        <Box component="li" {...props}>
          <Checkbox checked={selected} sx={{ mr: 1 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="body1">{option.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {[option.code, option.addressLine1 || option.address, option.city].filter(Boolean).join(' • ')}
            </Typography>
          </Box>
        </Box>
      )}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            label={option.name}
            size="small"
            {...getTagProps({ index })}
            sx={{
              backgroundColor: '#E8F5E9',
              color: '#1B5E20',
              '& .MuiChip-deleteIcon': {
                color: '#388E3C',
                '&:hover': { color: '#1B5E20' },
              },
            }}
          />
        ))
      }
      renderInput={(params) => (
        <MuiTextField
          {...params}
          label={translate('resources.promotions.fields.sites')}
          placeholder={translate('resources.promotions.helpers.sites')}
          helperText={translate('resources.promotions.restrictionHelpers.sitesHelper')}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

// Custom Charger Type Selector Component
const ChargerTypeSelector = () => {
  const { setValue, watch } = useFormContext();
  const dataProvider = useDataProvider();
  const translate = useTranslate();
  const [chargerTypes, setChargerTypes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const selectedTypeIds = watch('chargerTypes') || [];

  useEffect(() => {
    loadChargerTypes();
  }, []);

  const loadChargerTypes = async () => {
    try {
      setLoading(true);
      const { data } = await dataProvider.getList('charger-types', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'name', order: 'ASC' },
        filter: {},
      });
      setChargerTypes(data);
    } catch (error) {
      console.error('Error loading charger types:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectedTypes = chargerTypes.filter(type => selectedTypeIds.includes(type.id));

  return (
    <Autocomplete
      multiple
      options={chargerTypes}
      value={selectedTypes}
      onChange={(event, value) => {
        setValue('chargerTypes', value.map(type => type.id));
      }}
      loading={loading}
      getOptionLabel={(option) => option.name || ''}
      renderOption={(props, option, { selected }) => (
        <Box component="li" {...props}>
          <Checkbox checked={selected} sx={{ mr: 1 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="body1">{option.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {option.description || option.id}
            </Typography>
          </Box>
        </Box>
      )}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            label={option.name}
            size="small"
            {...getTagProps({ index })}
            sx={{
              backgroundColor: '#E3F2FD',
              color: '#0D47A1',
              '& .MuiChip-deleteIcon': {
                color: '#1976D2',
                '&:hover': { color: '#0D47A1' },
              },
            }}
          />
        ))
      }
      renderInput={(params) => (
        <MuiTextField
          {...params}
          label={translate('resources.promotions.fields.chargerTypes')}
          placeholder={translate('resources.promotions.restrictionHelpers.selectChargerType')}
          helperText={translate('resources.promotions.restrictionHelpers.chargerTypesHelper')}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

const validateRequired = required();
const validatePositive = minValue(0);

// Generate time options (00:00 to 23:45 in 15-minute intervals)
const generateTimeOptions = () => {
  const times = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      const hour = h.toString().padStart(2, '0');
      const minute = m.toString().padStart(2, '0');
      const timeValue = `${hour}:${minute}:00`;
      const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
      const period = h < 12 ? 'AM' : 'PM';
      const displayTime = m === 0
        ? `${hour}:${minute} (${displayHour}:${minute} ${period})`
        : `${hour}:${minute}`;
      times.push({ id: timeValue, name: displayTime });
    }
  }
  return times;
};

export const PromotionsCreate = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();
  const [serverErrors, setServerErrors] = React.useState<Record<string, string>>({});

  const transform = (data: any) => {
    // Convert camelCase to snake_case for API
    const transformedData: any = {
      name: data.name,
      description: data.description,
      code: data.code,
      promotion_type: data.promotionType,
      code_type: data.codeType,
      discount_type: data.discountType,
      discount_value: data.discountValue,
      max_discount_amount: data.maxDiscountAmount,
      user_type: data.userType,
      membership_levels: data.membershipLevels,
      valid_from: data.validFrom,
      valid_until: data.validUntil,
      time_of_day_start: data.timeOfDayStart,
      time_of_day_end: data.timeOfDayEnd,
      days_of_week: data.daysOfWeek,
      min_charge_amount: data.minChargeAmount,
      max_uses_total: data.maxUsesTotal,
      max_uses_per_user: data.maxUsesPerUser,
      priority: data.priority,
      stackable_with_coupons: data.stackableWithCoupons,
      stackable_with_promotions: data.stackableWithPromotions,
      is_active: data.isActive,
      translations: data.translations,
      terms: data.terms,
    };

    // Transform sites array from string IDs to objects with siteId and isExcluded
    if (data.sites && Array.isArray(data.sites)) {
      transformedData.sites = data.sites.map((siteId: string) => ({
        siteId,
        isExcluded: false,
      }));
    }

    // Add chargerTypes if present
    if (data.chargerTypes && data.chargerTypes.length > 0) {
      transformedData.chargerTypes = data.chargerTypes;
    }

    // Convert datetime to ISO 8601 with timezone (UTC)
    if (transformedData.valid_from) {
      const baseDate = transformedData.valid_from.split('.')[0].split('+')[0].split('Z')[0];
      transformedData.valid_from = baseDate.includes(':00:00') ? `${baseDate}Z` : `${baseDate}:00Z`;
    }
    if (transformedData.valid_until) {
      const baseDate = transformedData.valid_until.split('.')[0].split('+')[0].split('Z')[0];
      transformedData.valid_until = baseDate.includes(':00:00') ? `${baseDate}Z` : `${baseDate}:00Z`;
    }

    // Remove empty optional fields. Use strict null/undefined/empty-string
    // checks so a legitimately-entered numeric `0` (e.g. min_charge_amount = 0
    // meaning "no minimum") is forwarded to the backend rather than silently
    // dropped. We only strip values the user genuinely did not provide.
    const isMissing = (v: any) =>
      v === undefined || v === null || v === '' ||
      (Array.isArray(v) && v.length === 0);

    if (isMissing(transformedData.chargerTypes)) delete transformedData.chargerTypes;
    if (isMissing(transformedData.time_of_day_start)) delete transformedData.time_of_day_start;
    if (isMissing(transformedData.time_of_day_end)) delete transformedData.time_of_day_end;
    if (isMissing(transformedData.days_of_week)) delete transformedData.days_of_week;
    if (isMissing(transformedData.membership_levels)) delete transformedData.membership_levels;
    if (isMissing(transformedData.max_uses_total)) delete transformedData.max_uses_total;
    if (isMissing(transformedData.max_uses_per_user)) delete transformedData.max_uses_per_user;
    if (isMissing(transformedData.min_charge_amount)) delete transformedData.min_charge_amount;
    if (isMissing(transformedData.max_discount_amount)) delete transformedData.max_discount_amount;

    return transformedData;
  };

  return (
    <Create
      actions={false}
      title={false}
      component="div"
      redirect="list"
      mutationMode="pessimistic"
      transform={transform}
      mutationOptions={{
        onSuccess: () => {
          setServerErrors({});
          notify('resources.promotions.notifications.created', { type: 'success' });
          redirect('/promotions');
        },
        onError: (error: any) => {
          const apiError = error?.body || error;
          const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
          const fieldErrors: Record<string, string> = {};
          const summaryLines: string[] = [];
          if (rawErrors) {
            for (const [path, msgs] of Object.entries(rawErrors)) {
              const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
              const translated = translateServerMessage(rawMsg, translate);
              const field = mapPromotionBackendPath(path);
              fieldErrors[field] = translated;
              const label = translate(promotionFieldLabelKey(field), { _: field });
              summaryLines.push(`• ${label}: ${translated}`);
            }
          }
          setServerErrors(fieldErrors);

          const headerKey = apiError?.errorCode
            ? `errors.${apiError.errorCode}`
            : 'validation.formHasErrors';
          const header = translate(headerKey, { _: apiError?.message || 'Error' });

          if (summaryLines.length > 0) {
            notify(`${header}\n${summaryLines.join('\n')}`, {
              type: 'error',
              multiLine: true,
              autoHideDuration: 10000,
            });
          } else {
            notify(header, { type: 'error', autoHideDuration: 6000 });
          }
        },
      }}
      sx={{
        backgroundColor: 'transparent',
        '& .RaCreate-main': {
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },
      }}
    >
      <SimpleForm
        toolbar={<CreateToolbar />}
        sx={{
          '& .MuiPaper-root': {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
          '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
            px: { xs: 0, sm: 2 },
            py: { xs: 1, sm: 2 },
          },
          padding: { xs: 0, sm: 'inherit' },
        }}
      >
        <ServerErrorBinder errors={serverErrors} />
        <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', pb: { xs: 8, sm: 0 }, '& .MuiFormControl-root': { width: '100%' } }}>
          <PageHeader />

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              {translate('resources.promotions.create.createHint')}
            </Typography>
          </Alert>

          {/* Basic Information */}
          <Card sx={{ mb: 3, overflow: 'hidden' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <LocalOffer sx={{ mr: 2, color: '#00C853' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {translate('resources.promotions.sections.basicInfo')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextInput
                  source="name"
                  label={translate('resources.promotions.fields.name')}
                  validate={validateRequired}
                  fullWidth
                />
                <TextInput
                  source="description"
                  label={translate('resources.promotions.fields.description')}
                  multiline
                  rows={3}
                  fullWidth
                />
              </Box>
            </CardContent>
          </Card>

          {/* Translations */}
          <Card sx={{ mb: 3, overflow: 'hidden' }}>
            <CardContent>
              <FormDataConsumer>
                {({ formData }) => (
                  <PromotionMultiLanguageFields
                    formData={formData}
                    fields={[
                      {
                        name: 'name',
                        label: translate('resources.promotions.fields.name'),
                        required: true,
                      },
                      {
                        name: 'description',
                        label: translate('resources.promotions.fields.description'),
                        multiline: true,
                        rows: 3,
                      },
                    ]}
                  />
                )}
              </FormDataConsumer>
            </CardContent>
          </Card>

          {/* Terms & Conditions */}
          <Card sx={{ mb: 3, overflow: 'hidden' }}>
            <CardContent>
              <FormDataConsumer>
                {({ formData }) => (
                  <PromotionTermsManager formData={formData} />
                )}
              </FormDataConsumer>
            </CardContent>
          </Card>

          {/* Promotion Type & Code */}
          <Card sx={{ mb: 3, overflow: 'hidden' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Settings sx={{ mr: 2, color: '#00C853' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {translate('resources.promotions.sections.typeAndCode')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Alert severity="info" sx={{ mb: 1 }}>
                  <Typography variant="body2">
                    <strong>{translate('resources.promotions.types.coupon')}:</strong> {translate('resources.promotions.typeInfo.coupon')}<br />
                    <strong>{translate('resources.promotions.types.time_limited')}:</strong> {translate('resources.promotions.typeInfo.timeLimited')}
                  </Typography>
                </Alert>

                <RadioButtonGroupInput
                  source="promotionType"
                  label={translate('resources.promotions.fields.promotionType')}
                  choices={[
                    { id: 'coupon', name: translate('resources.promotions.types.coupon') },
                    { id: 'time_limited', name: translate('resources.promotions.types.time_limited') },
                  ]}
                  validate={validateRequired}
                  defaultValue="time_limited"
                  row
                />

                <FormDataConsumer>
                  {({ formData }) => {
                    if (formData.promotionType !== 'coupon') return null;

                    // Default to 'shared' if codeType is not set yet
                    const codeType = formData.codeType || 'shared';

                    return (
                      <>
                        <RadioButtonGroupInput
                          source="codeType"
                          label={translate('resources.promotions.fields.codeType')}
                          choices={[
                            { id: 'shared', name: translate('resources.promotions.codeTypes.shared') },
                            { id: 'unique', name: translate('resources.promotions.codeTypes.unique') },
                          ]}
                          validate={validateRequired}
                          defaultValue="shared"
                          helperText={translate('resources.promotions.helpers.codeType')}
                          row
                        />

                        {codeType === 'shared' && (
                          <TextInput
                            source="code"
                            label={translate('resources.promotions.fields.code')}
                            helperText={translate('resources.promotions.helpers.code')}
                            fullWidth
                            validate={validateRequired}
                          />
                        )}

                        {codeType === 'unique' && (
                          <Alert severity="info">
                            <Typography variant="body2">
                              Unique coupon codes will be generated after creating the promotion.
                              You can generate and manage codes from the promotion details page.
                            </Typography>
                          </Alert>
                        )}
                      </>
                    );
                  }}
                </FormDataConsumer>
              </Box>
            </CardContent>
          </Card>

          {/* Discount Configuration */}
          <Card sx={{ mb: 3, overflow: 'hidden' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <AttachMoney sx={{ mr: 2, color: '#00C853' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {translate('resources.promotions.sections.discount')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <RadioButtonGroupInput
                  source="discountType"
                  label={translate('resources.promotions.fields.discountType')}
                  choices={[
                    { id: 'percentage', name: translate('resources.promotions.discountTypes.percentage') },
                    { id: 'fixed_amount', name: translate('resources.promotions.discountTypes.fixed_amount') },
                    { id: 'free_credit', name: translate('resources.promotions.discountTypes.free_credit') },
                  ]}
                  validate={validateRequired}
                  defaultValue="percentage"
                  row
                />

                <FormDataConsumer>
                  {({ formData }) => {
                    const discountType = formData.discountType || 'percentage';
                    let label = translate('resources.promotions.fields.discountValue');
                    let helperText = '';
                    let placeholder = '';

                    if (discountType === 'percentage') {
                      label = translate('resources.promotions.discountFields.percentageLabel');
                      helperText = translate('resources.promotions.discountFields.percentageHelper');
                      placeholder = '50';
                    } else if (discountType === 'fixed_amount') {
                      label = translate('resources.promotions.discountFields.fixedAmountLabel');
                      helperText = translate('resources.promotions.discountFields.fixedAmountHelper');
                      placeholder = '100';
                    } else if (discountType === 'free_credit') {
                      label = translate('resources.promotions.discountFields.freeCreditLabel');
                      helperText = translate('resources.promotions.discountFields.freeCreditHelper');
                      placeholder = '50';
                    }

                    return (
                      <WheelSafeNumberInput
                        source="discountValue"
                        label={label}
                        helperText={helperText}
                        placeholder={placeholder}
                        validate={[validateRequired, validatePositive]}
                        fullWidth
                        sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                      />
                    );
                  }}
                </FormDataConsumer>

                <WheelSafeNumberInput
                  source="minChargeAmount"
                  label={translate('resources.promotions.fields.minChargeAmount')}
                  validate={validatePositive}
                  helperText={translate('resources.promotions.helpers.minChargeAmount')}
                  placeholder="0"
                  fullWidth
                  sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Target Users */}
          <Card sx={{ mb: 3, overflow: 'hidden' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <People sx={{ mr: 2, color: '#00C853' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {translate('resources.promotions.sections.targeting')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <RadioButtonGroupInput
                  source="userType"
                  label={translate('resources.promotions.fields.userType')}
                  choices={[
                    { id: 'app', name: translate('resources.promotions.userTypes.app') },
                    { id: 'rfid', name: translate('resources.promotions.userTypes.rfid') },
                    { id: 'all', name: translate('resources.promotions.userTypes.all') },
                  ]}
                  validate={validateRequired}
                  defaultValue="all"
                  row
                />

                <SelectArrayInput
                  source="membershipLevels"
                  label={translate('resources.promotions.fields.membershipLevels')}
                  choices={[
                    { id: 'standard', name: translate('resources.promotions.memberships.standard') },
                    { id: 'silver', name: translate('resources.promotions.memberships.silver') },
                    { id: 'gold', name: translate('resources.promotions.memberships.gold') },
                    { id: 'platinum', name: translate('resources.promotions.memberships.platinum') },
                    { id: 'vip', name: translate('resources.promotions.memberships.vip') },
                  ]}
                  helperText={translate('resources.promotions.helpers.membershipLevels')}
                  fullWidth
                  sx={{ '& .MuiInputBase-root': { minHeight: '56px' } }}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Validity Period */}
          <Card sx={{ mb: 3, overflow: 'hidden' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Schedule sx={{ mr: 2, color: '#00C853' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {translate('resources.promotions.sections.validity')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                  <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                    <DateTimeInput
                      source="validFrom"
                      label={translate('resources.promotions.fields.validFrom')}
                      validate={validateRequired}
                      fullWidth
                      sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                    />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                    <DateTimeInput
                      source="validUntil"
                      label={translate('resources.promotions.fields.validUntil')}
                      validate={validateRequired}
                      fullWidth
                      sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                    />
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#606060', mb: 1 }}>
                  {translate('resources.promotions.sections.timeConstraints')}
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                  <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                    <SelectInput
                      source="timeOfDayStart"
                      label={translate('resources.promotions.fields.timeOfDayStart')}
                      choices={generateTimeOptions()}
                      helperText={translate('resources.promotions.timeHelpers.leaveEmptyAllDay')}
                      fullWidth
                      sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                    />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                    <SelectInput
                      source="timeOfDayEnd"
                      label={translate('resources.promotions.fields.timeOfDayEnd')}
                      choices={generateTimeOptions()}
                      helperText={translate('resources.promotions.timeHelpers.leaveEmptyAllDay')}
                      fullWidth
                      sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                    />
                  </Box>
                </Box>

                <SelectArrayInput
                  source="daysOfWeek"
                  label={translate('resources.promotions.fields.daysOfWeek')}
                  choices={[
                    { id: 1, name: translate('resources.promotions.days.monday') },
                    { id: 2, name: translate('resources.promotions.days.tuesday') },
                    { id: 3, name: translate('resources.promotions.days.wednesday') },
                    { id: 4, name: translate('resources.promotions.days.thursday') },
                    { id: 5, name: translate('resources.promotions.days.friday') },
                    { id: 6, name: translate('resources.promotions.days.saturday') },
                    { id: 7, name: translate('resources.promotions.days.sunday') },
                  ]}
                  helperText={translate('resources.promotions.timeHelpers.leaveEmptyAllDays')}
                  fullWidth
                  sx={{ '& .MuiInputBase-root': { minHeight: '56px' } }}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Usage Limits */}
          <Card sx={{ mb: 3, overflow: 'hidden' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Block sx={{ mr: 2, color: '#00C853' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {translate('resources.promotions.sections.limits')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                  <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                    <WheelSafeNumberInput
                      source="maxUsesTotal"
                      label={translate('resources.promotions.fields.maxUsesTotal')}
                      validate={validatePositive}
                      helperText={translate('resources.promotions.helpers.maxUsesTotal')}
                      fullWidth
                      sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                    />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                    <WheelSafeNumberInput
                      source="maxUsesPerUser"
                      label={translate('resources.promotions.fields.maxUsesPerUser')}
                      validate={validatePositive}
                      helperText={translate('resources.promotions.helpers.maxUsesPerUser')}
                      fullWidth
                      sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                    />
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Advanced Settings */}
          <Card sx={{ mb: 3, overflow: 'hidden' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <TrendingUp sx={{ mr: 2, color: '#00C853' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {translate('resources.promotions.sections.advanced')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <SelectInput
                  source="priority"
                  label={translate('resources.promotions.fields.priority')}
                  choices={[
                    { id: 1, name: translate('resources.promotions.priorityLevels.1') },
                    { id: 2, name: translate('resources.promotions.priorityLevels.2') },
                    { id: 3, name: translate('resources.promotions.priorityLevels.3') },
                    { id: 4, name: translate('resources.promotions.priorityLevels.4') },
                    { id: 5, name: translate('resources.promotions.priorityLevels.5') },
                    { id: 6, name: translate('resources.promotions.priorityLevels.6') },
                    { id: 7, name: translate('resources.promotions.priorityLevels.7') },
                    { id: 8, name: translate('resources.promotions.priorityLevels.8') },
                    { id: 9, name: translate('resources.promotions.priorityLevels.9') },
                    { id: 10, name: translate('resources.promotions.priorityLevels.10') },
                  ]}
                  defaultValue={5}
                  helperText={translate('resources.promotions.helpers.priority')}
                  fullWidth
                  sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                />

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 0, sm: 2 }, alignItems: { xs: 'stretch', sm: 'center' } }}>
                  <BooleanInput
                    source="stackableWithCoupons"
                    label={translate('resources.promotions.fields.stackableWithCoupons')}
                    defaultValue={true}
                  />
                  <BooleanInput
                    source="stackableWithPromotions"
                    label={translate('resources.promotions.fields.stackableWithPromotions')}
                    defaultValue={false}
                  />
                  <BooleanInput
                    source="isActive"
                    label={translate('resources.promotions.fields.isActive')}
                    defaultValue={true}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Site and Charger Type Restrictions */}
          <Card sx={{ mb: 3, overflow: 'hidden' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <LocationOn sx={{ mr: 2, color: '#00C853' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {translate('resources.promotions.sections.restrictions')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#606060', mb: 1 }}>
                    {translate('resources.promotions.fields.sites')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {translate('resources.promotions.helpers.sites')}
                  </Typography>
                  <SiteSelector />
                </Box>

                <Divider />

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#606060', mb: 1 }}>
                    {translate('resources.promotions.fields.chargerTypes')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {translate('resources.promotions.restrictionHelpers.chargerTypesHelper')}
                  </Typography>
                  <ChargerTypeSelector />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </SimpleForm>
    </Create>
  );
};